import './question-flow.css';

/**
 * A form that grows one QUESTION at a time. Each step is a question heading
 * (+ optional help) over its own fields; answering it reveals the next. Earlier
 * questions stay as ordinary, editable fields — nothing collapses — so editing
 * an existing item is simply every question revealed at once (`revealAll`).
 *
 * Steps (in order):
 *   { id, question, help?, children: Node[],
 *     applies?: () => boolean,   // is this question relevant to the answers so far?
 *     answered: () => boolean,   // does it hold a valid answer?
 *     optional?: boolean,        // answered-when-blank; its Next reads "Skip" while blank
 *     joinPrevious?: boolean }   // a follow-up: no divider between it and the question before
 *
 * Revealed questions are separated by a divider, except a `joinPrevious` one,
 * which reads as part of the question it follows.
 *
 * The NEWEST revealed question (the "frontier") shows a Next button (Enter in
 * one of its inputs does the same); Next is disabled until it's answered. A
 * CHOICE question advances itself: call `advance(id)` from its onChange. Later
 * questions stay hidden until the frontier is passed; a question that stops
 * applying hides (its fields keep their values). The last applicable question
 * has no Next — the form's own submit is the final action.
 *
 * Any input/change inside re-syncs and calls `onChange()` (e.g. to refresh a
 * live result or gate submit on `isComplete()`).
 *
 * @returns {{ el: HTMLElement, start(): void, revealAll(): void, advance(id): void,
 *   sync(): void, isComplete(): boolean, section(id): HTMLElement }}
 */
export function createQuestionFlow({ steps, onChange } = {}) {
  const el = document.createElement('div');
  el.className = 'question-flow';
  const passed = new Set();
  const parts = new Map();

  for (const step of steps) {
    const section = document.createElement('section');
    section.className = step.joinPrevious ? 'question question--joined' : 'question';
    section.dataset.question = step.id;
    const title = document.createElement('h4');
    title.className = 'question__title';
    title.textContent = step.question;
    section.append(title);
    if (step.help) {
      const help = document.createElement('p');
      help.className = 'question__help';
      help.textContent = step.help;
      section.append(help);
    }
    section.append(...(step.children ?? []));
    const nextRow = document.createElement('div');
    nextRow.className = 'question__next';
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'secondary small';
    next.textContent = 'Next';
    next.addEventListener('click', () => advance(step.id));
    nextRow.append(next);
    section.append(nextRow);
    section.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || passed.has(step.id)) return;
      if (!(e.target instanceof HTMLInputElement)) return;
      e.preventDefault(); // don't submit the form from an unfinished question
      advance(step.id);
    });
    parts.set(step.id, { step, section, nextRow, next });
    el.append(section);
  }

  el.addEventListener('input', () => changed());
  el.addEventListener('change', () => changed());

  const applies = (step) => (step.applies ? step.applies() : true);
  const answered = (step) => !!step.answered();

  function changed() {
    sync();
    onChange?.();
  }

  /** Show every applicable question up to (and including) the frontier. */
  function sync() {
    const applicable = steps.filter(applies);
    const last = applicable.at(-1);
    let stopped = false;
    for (const step of steps) {
      const { section, nextRow, next } = parts.get(step.id);
      const show = !stopped && applicable.includes(step);
      section.hidden = !show;
      if (!show) continue;
      const frontier = !passed.has(step.id);
      nextRow.hidden = !frontier || step === last;
      next.disabled = !answered(step);
      next.textContent = step.optional && isBlank(section) ? 'Skip' : 'Next';
      if (frontier) stopped = true;
    }
  }

  /** Mark a question answered and move on to the next one (focusing it). */
  function advance(id) {
    const part = parts.get(id);
    if (!part || !answered(part.step)) {
      sync();
      return;
    }
    const wasFrontier = !passed.has(id);
    passed.add(id);
    sync();
    onChange?.();
    if (wasFrontier) focusFrontier();
  }

  function focusFrontier() {
    const step = steps.find((s) => applies(s) && !passed.has(s.id));
    if (!step) return;
    const { section } = parts.get(step.id);
    const target =
      section.querySelector('input:not([type="hidden"]), select, textarea') ??
      section.querySelector('[role="radio"][tabindex="0"], button');
    target?.focus();
  }

  return {
    el,
    /** A fresh form: only the first question shows. */
    start() {
      passed.clear();
      sync();
    },
    /** An existing item: every applicable question shows. */
    revealAll() {
      for (const step of steps) passed.add(step.id);
      sync();
    },
    advance,
    sync,
    /** Every applicable question is revealed and answered. */
    isComplete() {
      const applicable = steps.filter(applies);
      const frontier = applicable.findIndex((s) => !passed.has(s.id));
      const revealed = frontier === -1 || frontier === applicable.length - 1;
      return revealed && applicable.every(answered);
    },
    section: (id) => parts.get(id)?.section ?? null,
  };
}

function isBlank(section) {
  return [...section.querySelectorAll('input, textarea')].every((i) => !i.value.trim());
}
